import type { ViewProps } from "./types";
import { StatIcon } from "../parts/icons";
import { formatNumber } from "../parts/format";
import { useCountUp } from "../useCountUp";
import { shade } from "../parts/StatItem";

/**
 * Single-row inline render: ⚡ 50 AC · ⚡ 189 DC · 📍 63 Lokasyon ...
 * Excellent for header bars or hero strips on a host site.
 */
export function InlineView({ cfg, items }: ViewProps) {
  const padY = cfg.density === "compact" ? "py-2" : cfg.density === "comfortable" ? "py-4" : "py-3";
  const justify =
    cfg.align === "left" ? "justify-start" : cfg.align === "right" ? "justify-end" : "justify-center";
  return (
    <div className={"flex h-full items-center " + justify + " " + padY + " px-4"}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 sm:gap-x-7">
        {items.map((it, i) => (
          <Inline key={it.k} k={it.k} value={it.value} label={it.label} accent={cfg.accent} delayMs={i * 60} cfg={cfg} />
        ))}
      </div>
    </div>
  );
}

function Inline({
  k,
  value,
  label,
  accent,
  delayMs,
  cfg,
}: {
  k: ViewProps["items"][number]["k"];
  value: number;
  label: string;
  accent: string;
  delayMs: number;
  cfg: ViewProps["cfg"];
}) {
  const animated = useCountUp(value, cfg.animate ? 1100 : 0);
  return (
    <div
      className="flex items-center gap-2 animate-fadeIn"
      style={{ animationDelay: cfg.animate ? `${delayMs}ms` : "0ms", animationFillMode: "both" }}
    >
      <span
        className="grid h-6 w-6 place-items-center rounded-md text-white"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, ${shade(accent, -0.22)} 100%)` }}
        aria-hidden
      >
        <StatIcon k={k} className="h-3.5 w-3.5" />
      </span>
      <span
        className="tabular-nums font-semibold"
        style={{
          color: "var(--lm-fg)",
          fontSize: cfg.size === "lg" ? 22 : cfg.size === "sm" ? 16 : 18,
          lineHeight: 1.1,
        }}
      >
        {formatNumber(animated, cfg.format, cfg.locale)}
      </span>
      <span
        className="font-medium"
        style={{
          color: "var(--lm-label)",
          fontSize: cfg.size === "lg" ? 13 : 11,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
    </div>
  );
}
